// Modal mit Email-Input + Role-Select. Auf Submit: API-Call → zeigt
// generierte Invite-URL mit Copy-Button (Doc kopiert manuell und mailt).

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Copy, Loader2, UserPlus, X } from 'lucide-react';
import type { CreateUserResponse, UserRole } from '@/lib/admin-users-api';

interface AddMemberDialogProps {
  onClose: () => void;
  onCreate: (input: { email: string; role: UserRole }) => Promise<CreateUserResponse>;
}

export function AddMemberDialog({ onClose, onCreate }: AddMemberDialogProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('trial');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreateUserResponse | null>(null);
  const [copied, setCopied] = useState(false);

  async function submit() {
    const e = email.trim().toLowerCase();
    if (!e.includes('@')) {
      setError('Gültige Email erforderlich.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await onCreate({ email: e, role });
      setCreated(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }

  async function copyUrl() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.invite_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Invite-URL kopieren:', created.invite_url);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-strong w-[min(480px,92vw)] rounded-2xl border border-amber-500/20 p-5"
      >
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserPlus className="size-5 text-amber-300" />
            <h2 className="text-base font-bold text-amber-200">Member hinzufügen</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-neutral-500 hover:bg-white/5 hover:text-neutral-200"
          >
            <X className="size-4" />
          </button>
        </div>

        {!created ? (
          <>
            <p className="mb-4 text-[12px] text-neutral-400">
              User wird angelegt + Magic-Link mit 7-Tage-Ablauf generiert. Du kopierst die URL und schickst sie manuell.
            </p>
            <label className="block text-[11px] uppercase tracking-wider text-neutral-500">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="mario@maler.de"
              className="mb-3 mt-1 w-full rounded-md border border-white/10 bg-neutral-900/60 px-3 py-1.5 text-[12px] text-neutral-100 outline-none focus:border-amber-500/40"
            />
            <label className="block text-[11px] uppercase tracking-wider text-neutral-500">Tier</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="mb-3 mt-1 w-full rounded-md border border-white/10 bg-neutral-900/60 px-3 py-1.5 text-[12px] text-neutral-100 outline-none focus:border-amber-500/40"
            >
              <option value="free">Free</option>
              <option value="trial">Trial</option>
              <option value="pro">Pro</option>
            </select>
            {error ? (
              <p className="mb-2 text-[11px] text-rose-300">{error}</p>
            ) : null}
            <button
              type="button"
              onClick={() => void submit()}
              disabled={saving || email.trim().length === 0}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/15 px-3 py-1.5 text-[12px] font-semibold text-amber-200 hover:bg-amber-500/25 disabled:opacity-40"
            >
              {saving ? <Loader2 className="size-3 animate-spin" /> : <UserPlus className="size-3" />}
              Anlegen + Invite generieren
            </button>
          </>
        ) : (
          <>
            <p className="mb-2 text-[12px] text-emerald-300">
              ✓ <span className="text-neutral-100">{created.email}</span> angelegt.
            </p>
            <p className="mb-3 text-[12px] text-neutral-400">
              Schicke diese URL per Mail/Slack/WA. Gilt 7 Tage.
            </p>
            <div className="mb-2 flex items-center gap-2 rounded-md border border-white/10 bg-neutral-900/60 px-2 py-1.5">
              <code className="flex-1 truncate font-mono text-[11px] text-neutral-300">
                {created.invite_url}
              </code>
              <button
                type="button"
                onClick={() => void copyUrl()}
                className="inline-flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/15 px-2 py-1 text-[11px] font-bold text-amber-300 hover:bg-amber-500/25"
              >
                {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
                {copied ? 'Kopiert' : 'Kopieren'}
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-md border border-white/10 bg-white/[0.02] px-3 py-1.5 text-[12px] text-neutral-300 hover:bg-white/[0.05]"
            >
              Fertig
            </button>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
